import { auditService } from '@/services';
import type {
  ListAuditActivitiesParams,
  ListAuditActivitiesResponse,
} from '@/services';
import { useInfiniteQuery } from '@tanstack/react-query';

/** Página cheia: a API aceita até 200 e o padrão dela é 50. */
const PAGE_SIZE = 50;

export const auditActivitiesQueryKey = (
  params: ListAuditActivitiesParams | null
) => ['audit-activities', params] as const;

/**
 * Trilha de auditoria, paginada por cursor do DynamoDB.
 *
 * `params === null` significa recorte incompleto — a API responderia 400, então
 * a query fica desabilitada e a tela pede o que falta.
 *
 * Atenção ao filtro por AÇÃO: o BFF o aplica DEPOIS da Query, sobre a página
 * já lida. Uma página pode voltar vazia e ainda assim trazer `nextCursor` —
 * por isso "Carregar mais" depende de `hasNextPage`, nunca da contagem de
 * itens na tela.
 */
export function useAuditActivities(params: ListAuditActivitiesParams | null) {
  return useInfiniteQuery({
    queryKey: auditActivitiesQueryKey(params),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      // O `enabled` já garante isto; o guard existe para o tipo.
      if (!params) {
        return { items: [], nextCursor: null } as ListAuditActivitiesResponse;
      }

      const result = await auditService.listActivities({
        ...params,
        limit: PAGE_SIZE,
        cursor: pageParam,
      });

      if (!result.success || !result.data) {
        throw new Error(
          result.error ?? 'Não foi possível carregar a trilha de auditoria.'
        );
      }

      return result.data;
    },
    getNextPageParam: (lastPage: ListAuditActivitiesResponse) =>
      lastPage.nextCursor ?? undefined,
    // A trilha é append-only: o que já foi lido não muda.
    staleTime: 60_000,
    enabled: params !== null,
  });
}
